import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';
import { starterRecipeAdminService, StarterRecipe } from '@/services/starterRecipe/starterRecipeAdminService';
import { ImageUpload } from '@/components/ui/image-upload';

interface StarterRecipeEditDialogProps {
  recipe: StarterRecipe;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  isNew?: boolean;
}

const StarterRecipeEditDialog: React.FC<StarterRecipeEditDialogProps> = ({
  recipe,
  open,
  onOpenChange,
  onSuccess,
  isNew = false,
}) => {
  // Form state
  const [title, setTitle] = useState(recipe.title);
  const [description, setDescription] = useState(recipe.description || '');
  const [prepTime, setPrepTime] = useState(recipe.prep_time || '');
  const [cookTime, setCookTime] = useState(recipe.cook_time || '');
  const [totalTime, setTotalTime] = useState(recipe.total_time || '');
  const [servings, setServings] = useState(recipe.servings || '');
  const [sourceUrl, setSourceUrl] = useState(recipe.source_url || '');
  const [imageUrl, setImageUrl] = useState(recipe.image_url || recipe.storage_image_url || '');
  const [tags, setTags] = useState<string[]>(recipe.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [instructions, setInstructions] = useState<string[]>(recipe.instructions || []);
  const [isActive, setIsActive] = useState(recipe.is_active);
  const [displayOrder, setDisplayOrder] = useState(recipe.display_order);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: () => starterRecipeAdminService.createStarterRecipe({
      title,
      description: description || null,
      ingredients: recipe.ingredients || [],
      instructions,
      prep_time: prepTime || null,
      cook_time: cookTime || null,
      total_time: totalTime || null,
      servings: servings || null,
      source_url: sourceUrl || null,
      image_url: imageUrl || null,
      storage_image_url: recipe.storage_image_url || null,
      tags: tags.length > 0 ? tags : null,
      structured_ingredients_metric: recipe.structured_ingredients_metric,
      structured_ingredients_imperial: recipe.structured_ingredients_imperial,
      imperial_version: recipe.imperial_version,
      metric_version: recipe.metric_version,
      base_measurement_system: recipe.base_measurement_system,
      is_active: isActive,
      display_order: displayOrder,
    }),
    onSuccess: () => {
      toast.success('Starter recipe created!');
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create recipe: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: () => starterRecipeAdminService.updateStarterRecipe(recipe.id, {
      title,
      description: description || null,
      instructions,
      prep_time: prepTime || null,
      cook_time: cookTime || null,
      total_time: totalTime || null,
      servings: servings || null,
      source_url: sourceUrl || null,
      image_url: imageUrl || null,
      tags: tags.length > 0 ? tags : null,
      is_active: isActive,
      display_order: displayOrder,
    }),
    onSuccess: () => {
      toast.success('Starter recipe updated!');
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update recipe: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (isNew) {
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleAddInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const handleUpdateInstruction = (index: number, value: string) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };

  const handleRemoveInstruction = (index: number) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'Create Starter Recipe' : 'Edit Starter Recipe'}
          </DialogTitle>
          <DialogDescription>
            {isNew 
              ? 'Review and modify the imported recipe before saving.' 
              : 'Make changes to this starter recipe.'
            }
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-8rem)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 py-2">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Recipe title"
                className="focus-visible:ring-primary"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the recipe"
                rows={2}
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Recipe Image</Label>
              <ImageUpload
                recipeId={recipe.id || 'starter-temp'}
                currentImage={imageUrl}
                onUserImageUploaded={(url) => setImageUrl(url)}
                onError={(error) => toast.error(error)}
              />
            </div>

            {/* Or Image URL */}
            <div className="space-y-2">
              <Label htmlFor="imageUrl">Or paste Image URL</Label>
              <Input
                id="imageUrl"
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              {imageUrl && (
                <div className="mt-2 rounded-lg overflow-hidden border">
                  <img 
                    src={imageUrl} 
                    alt="Recipe preview" 
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Time and Servings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prepTime">Prep Time</Label>
                <Input
                  id="prepTime"
                  value={prepTime}
                  onChange={(e) => setPrepTime(e.target.value)}
                  placeholder="15 minutes"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cookTime">Cook Time</Label>
                <Input
                  id="cookTime"
                  value={cookTime}
                  onChange={(e) => setCookTime(e.target.value)}
                  placeholder="30 minutes"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totalTime">Total Time</Label>
                <Input
                  id="totalTime"
                  value={totalTime}
                  onChange={(e) => setTotalTime(e.target.value)}
                  placeholder="45 minutes"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="servings">Servings</Label>
                <Input
                  id="servings"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                  placeholder="4 servings"
                />
              </div>
            </div>

            {/* Source URL */}
            <div className="space-y-2">
              <Label htmlFor="sourceUrl">Source URL</Label>
              <Input
                id="sourceUrl"
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://example.com/original-recipe"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Instructions</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddInstruction}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Step
                </Button>
              </div>
              <div className="space-y-2">
                {instructions.map((instruction, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <span className="text-sm text-muted-foreground mt-2 w-6">
                      {index + 1}.
                    </span>
                    <Textarea
                      value={instruction}
                      onChange={(e) => handleUpdateInstruction(index, e.target.value)}
                      placeholder={`Step ${index + 1}`}
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveInstruction(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {instructions.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">
                    No instructions added yet.
                  </p>
                )}
              </div>
            </div>

            {/* Display Order */}
            <div className="space-y-2">
              <Label htmlFor="displayOrder">Display Order</Label>
              <Input
                id="displayOrder"
                type="number"
                min="0"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first in the list.
              </p>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Activate Recipe</p>
                <p className="text-sm text-muted-foreground">
                  Active recipes are visible to new users
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isNew ? 'Creating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isNew ? 'Create Recipe' : 'Save Changes'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default StarterRecipeEditDialog;
